/**
 * SSE client for POST /rag/generate-media/stream (flyer edit with + upload).
 */

import {
    mapTemplateProgressToMessage,
    parseSseChunk,
    normalizeStreamEvent,
    type TemplateStreamEvent,
    type TemplateStreamHandler,
} from "./templateGenerationStream";

export type FlyerMediaStreamEvent = TemplateStreamEvent;
export type FlyerMediaStreamHandler = TemplateStreamHandler;

export { mapTemplateProgressToMessage };

export async function streamGenerateFlyerMedia(
    url: string,
    headers: Record<string, string>,
    body: Record<string, unknown>,
    onEvent: FlyerMediaStreamHandler,
): Promise<void> {
    const res = await fetch(url, {
        method: "POST",
        headers: {
            ...headers,
            Accept: "text/event-stream",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        // Never surface a raw status code to the user — reads as a broken app.
        const FRIENDLY_FALLBACK = "We are experiencing high volume right now. Please try again in a moment.";
        let detail: Record<string, unknown> = { message: FRIENDLY_FALLBACK };
        try {
            const json = await res.json();
            const parsed = (json.detail as Record<string, unknown>) ?? json;
            if (parsed && typeof parsed === "object" && typeof parsed.message === "string" && parsed.message.trim()) {
                detail = parsed;
            }
        } catch {
            /* ignore — keep friendly fallback */
        }
        // subscription_error_handler (backend) keys the quota code as `error`,
        // not `code` — normalize so callers reading detail_payload.code (e.g.
        // GeneratorPage's ErrorBanner) see DAILY_QUOTA_EXCEEDED/MONTHLY_QUOTA_EXCEEDED
        // the same way the axios-based session endpoints already do via extractQuotaError.
        const rawCode =
            (typeof detail.code === "string" && detail.code) ||
            (typeof detail.error === "string" && detail.error) ||
            "";
        if (detail.code == null && rawCode) {
            detail = { ...detail, code: rawCode };
        }
        // Same detection RagChatPage's maybeEmitQuotaFromStream/apiErrorResponse use:
        // the error code is the source of truth for "is this a quota wall", not the
        // raw HTTP status — go by the code (or message text) it actually carries.
        const isQuota = rawCode ? /quota/i.test(rawCode) : /quota|limit/i.test(String(detail.message ?? ""));
        onEvent("error", {
            stage: "error",
            title: isQuota ? "Daily limit reached ✋" : "System is busy",
            message: String(detail.message ?? FRIENDLY_FALLBACK),
            detail: String(detail.message ?? FRIENDLY_FALLBACK),
            detail_payload: detail,
            status: res.status,
            progress: 0,
            phase: "done",
        });
        return;
    }

    const reader = res.body?.getReader();
    if (!reader) {
        onEvent("error", {
            stage: "error",
            title: "System is busy",
            message: "We are experiencing high volume right now. Please try again in a moment.",
            progress: 0,
            phase: "done",
        });
        return;
    }

    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseChunk(buffer);
        buffer = rest;
        for (const { event, data } of events) {
            let parsed: Record<string, unknown> = {};
            try {
                parsed = JSON.parse(data) as Record<string, unknown>;
            } catch {
                parsed = { message: data };
            }
            onEvent(normalizeStreamEvent(event), parsed);
        }
    }
}
